// client/src/components/VerificationError.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import ApiService from '../services/api';
import { notify } from '../utils/notifications';

function VerificationError({ platform }) {
  const { isConnected, account, completeVerificationWithSignature } = useWeb3();
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); 
  const navigate = useNavigate();

  useEffect(() => {
    if (!isConnected) {
      navigate('/');
      return;
    }

    async function checkAndCompleteVerification() {
      try {
        let status;
        
        if (platform === 'twitter') {
          status = await ApiService.getTwitterVerificationStatus();
        } else {
          notify.errorWithSuggestions(
            `Verification for ${formatPlatformName(platform)} is not yet supported`,
            'unsupported_platform',
            ['This platform is coming soon', 'Try Twitter verification instead']
          );
          setTimeout(() => navigate('/passport'), 2000);
          return;
        }

        if (!status.verified) {
          navigate(`/verification/${platform}`);
          return;
        }

        setVerificationData(status);
        setLoading(false);
        
        setTimeout(() => {
          sessionStorage.setItem(`${platform}_completing`, 'true');
          completeVerification();
        }, 500);
      } catch (error) {
        console.error('Error checking verification status:', error);
        setLoading(false);
        setVerificationStatus('error');
        
        notify.errorWithSuggestions(
          'Failed to verify status. Please try again.',
          'network',
          ['Check your internet connection', 'Refresh the page']
        );
      }
    }

    const wasCompleting = sessionStorage.getItem(`${platform}_completing`) === 'true';
    const isVerified = sessionStorage.getItem(`${platform}_verified`) === 'true';
    
    if (isVerified) {
      setVerificationStatus('success');
      setCompleted(true);
      setLoading(false);
    } else if (wasCompleting) {
      setLoading(false);
    } else {
      checkAndCompleteVerification();
    }

    return () => {
      if (completed && verificationStatus === 'success') {
        sessionStorage.setItem(`${platform}_verified`, 'true');
        sessionStorage.removeItem(`${platform}_completing`);
      }
    };
  }, [isConnected, navigate, platform, completed, verificationStatus]);

  const completeVerification = async () => {
    if (completing || completed) return;
    
    setCompleting(true);
    setVerificationStatus('pending');

    try {
      if (platform === 'twitter') {
        await new Promise(resolve => setTimeout(resolve, 100));
        const result = await completeVerificationWithSignature(platform);
        
        console.log("Verification complete, result:", result);
        
        if (result && result.success) {
          setCompleted(true);
          setVerificationStatus('success');
          sessionStorage.setItem(`${platform}_verified`, 'true');
          sessionStorage.removeItem(`${platform}_completing`);
          
          // Success notification
          notify.success(`🎉 ${formatPlatformName(platform)} verification complete!`, {
            duration: 3000
          });
          
          setTimeout(() => {
            navigate('/passport');
          }, 3000);
        } else if (result && result.alreadyVerified) {
          setCompleted(true);
          setVerificationStatus('success');
          sessionStorage.setItem(`${platform}_verified`, 'true');
          sessionStorage.removeItem(`${platform}_completing`);
          
          // Already verified notification
          notify.info(`✅ ${formatPlatformName(platform)} account already verified!`, {
            duration: 3000
          });
          
          setTimeout(() => {
            navigate('/passport');
          }, 3000);
        } else if (result && result.error && result.error.includes("Account already claimed")) {
          setVerificationStatus('already_claimed');
          sessionStorage.removeItem(`${platform}_completing`);
          
          // Account claimed notification
          notify.errorWithSuggestions(
            `This ${formatPlatformName(platform)} account has already been verified by another wallet address.`,
            'account_claimed',
            [
              'Each social account can only be linked to one wallet',
              'Try using a different social account',
              'Contact support if you believe this is an error'
            ]
          );
        } else {
          setVerificationStatus('error');
          sessionStorage.removeItem(`${platform}_completing`);
          
          notify.errorWithSuggestions(
            result?.error || "Verification failed without specific error",
            'verification_failed',
            ['Please try again', 'Check your internet connection']
          );
        }
      } else {
        setVerificationStatus('error');
        
        notify.errorWithSuggestions(
          `Verification for ${formatPlatformName(platform)} is not yet supported`,
          'unsupported_platform',
          ['This platform is coming soon', 'Try Twitter verification instead']
        );
      }
    } catch (err) {
      console.error('Completion error:', err);
      
      if (err.code === 4001) { 
        setVerificationStatus('error');
        sessionStorage.removeItem(`${platform}_completing`);
        
        notify.warning('⚠️ Signature required to complete verification', {
          duration: 5000
        });
      } else {
        let errorMessage = err.message || `Failed to complete ${platform} verification`;
        
        if (errorMessage.includes("Account already claimed")) {
          setVerificationStatus('already_claimed');
          sessionStorage.removeItem(`${platform}_completing`);
          
          notify.errorWithSuggestions(
            `This ${formatPlatformName(platform)} account has already been verified by another wallet address.`,
            'account_claimed',
            [
              'Each social account can only be linked to one wallet',
              'Try using a different social account'
            ]
          );
        } else if (errorMessage.includes("already verified") || errorMessage.includes("Verification for this platform already exists")) {
          setCompleted(true);
          setVerificationStatus('success');
          sessionStorage.setItem(`${platform}_verified`, 'true');
          sessionStorage.removeItem(`${platform}_completing`);
          
          notify.info(`✅ ${formatPlatformName(platform)} account already verified!`);
          
          setTimeout(() => {
            navigate('/passport');
          }, 3000);
          return;
        } else if (errorMessage.toLowerCase().includes("success") || 
                  errorMessage.includes("transaction successful")) {
          setCompleted(true);
          setVerificationStatus('success');
          sessionStorage.setItem(`${platform}_verified`, 'true');
          sessionStorage.removeItem(`${platform}_completing`);
          
          notify.success(`🎉 ${formatPlatformName(platform)} verification complete!`);
          
          setTimeout(() => {
            navigate('/passport');
          }, 3000);
          return;
        } else {
          setVerificationStatus('error');
          sessionStorage.removeItem(`${platform}_completing`);
          
          // Determine error category based on error message
          let errorCategory = 'verification_failed';
          const suggestions = ['Please try again', 'Check your internet connection'];
          
          if (errorMessage.includes('network') || errorMessage.includes('connection')) {
            errorCategory = 'network';
            suggestions.push('Check your internet connection');
          } else if (errorMessage.includes('unauthorized') || errorMessage.includes('not authorized')) {
            errorCategory = 'unauthorized';
            suggestions.push('Make sure you\'re signed in to your wallet');
          } else if (errorMessage.includes('rate limit') || errorMessage.includes('too many requests')) {
            errorCategory = 'rate_limit';
            suggestions.push('Wait a few minutes before trying again');
          }
          
          notify.errorWithSuggestions(errorMessage, errorCategory, suggestions);
        }
      }
    } finally {
      setCompleting(false);
    }
  };

  const formatPlatformName = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  if (loading) {
    return (
      <div className="verification-container">
        <div className="glass-card loading-card">
          <div className="loader"></div>
          <p className="loading-text">Checking {formatPlatformName(platform)} verification status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <div className="hero-section">
        <h1 className="gradient-title">Verification Status</h1>
        <p className="subtitle">
          Managing your social account verification
        </p>
      </div>

      <div className="glass-card verification-card">
        <div className="text-center mb-6">
          {verificationStatus === 'already_claimed' ? (
            <div className="app-icon app-icon-warning">
              <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
          ) : verificationStatus === 'error' ? (
            <div className="app-icon app-icon-error">
              <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
          ) : verificationStatus === 'success' ? (
            <div className="app-icon app-icon-success">
              <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          ) : (
            <div className="app-icon app-icon-info">
              <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          )}
          
          <h2 className="card-title">
            {verificationStatus === 'already_claimed' ? (
              "Account Already Verified"
            ) : verificationStatus === 'error' ? (
              "Verification Error"
            ) : verificationStatus === 'success' ? (
              "Verification Complete!"
            ) : (
              "Processing Verification"
            )}
          </h2>
          
          <p className="card-text">
            {verificationStatus === 'already_claimed' ? (
              "This social account is already linked to another wallet. Check the notification for details."
            ) : verificationStatus === 'error' ? (
              "There was a problem with the verification. Check the notification for details and next steps."
            ) : verificationStatus === 'success' ? (
              `Your ${formatPlatformName(platform)} account has been verified on-chain.`
            ) : (
              `Processing your ${formatPlatformName(platform)} verification. This may take a moment.`
            )}
          </p>
        </div>

        {verificationData && (
          <div className="verification-info">
            <h3 className="info-title">Verification Details:</h3>
            <div className="detail-item">
              <div className="detail-label">Platform</div>
              <div className="detail-value">{formatPlatformName(platform)}</div>
            </div>
            {platform === 'twitter' && (
              <div className="detail-item">
                <div className="detail-label">Username</div>
                <div className="detail-value">@{verificationData.username}</div>
              </div>
            )}
          </div>
        )}

        {verificationStatus === 'success' ? (
          <div className="text-center">
            <div className="alert-success">
              <p>Verification has been recorded on-chain!</p>
            </div>
            <p className="card-text mt-4">
              Redirecting to your passport...
            </p>
            <div className="card-footer">
              <Link
                to="/passport"
                className="btn btn-gradient"
              >
                View My Passport
              </Link>
            </div>
          </div>
        ) : (
          <div className="card-footer">
            {completing ? (
              <div className="creating-passport-state">
                <div className="loader"></div>
                <p className="card-text">Recording verification on-chain...</p>
                <p className="card-subtext">
                  You'll need to sign a message with your wallet to verify ownership.
                </p>
              </div>
            ) : verificationStatus === 'already_claimed' ? (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/" className="btn btn-gradient">
                  Return Home
                </Link>
                <Link to={`/verification/${platform}`} className="btn btn-outline">
                  Try Different Account
                </Link>
              </div>
            ) : verificationStatus === 'error' ? (
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={completeVerification}
                  className="btn btn-gradient"
                >
                  Try Again
                </button>
                <Link to={`/verification/${platform}`} className="btn btn-outline">
                  Start Over
                </Link>
              </div>
            ) : (
              <button
                onClick={completeVerification}
                className="btn btn-gradient"
              >
                Complete Verification
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VerificationError;